import {
  IMerchant,
  IMerchantTableColumns,
  useCreateMerchantSettingsMutation,
  useDeleteMerchantMutation,
  useEditMerchantMutation,
} from '@entities/merchant';
import { ModalDeleteEntity } from '@features/modal-entity-delete';
import { ModalEditEntity } from '@features/modal-entity-edit';
import { ModalViewEntity } from '@features/modal-entity-view';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';

import { NewMerchantModal } from '../components/NewMerchant';
import { assmebleMerchantSettings } from '../utils/assembleMerchantSettings';

interface IProps {
  refetch?: () => void;
}

export const useTableSettings = ({ refetch }: IProps) => {
  const [deleteMerchant] = useDeleteMerchantMutation();

  // THead settings
  const columnTitles: IMerchantTableColumns = {
    domain: 'Domain',
    created_at: 'Created At',
    updated_at: 'Updated At',
    short_name: 'Short Name',
    actions: 'Actions',
  };

  // TBody settings
  const renderRow = (key: keyof IMerchantTableColumns, merchant: IMerchant) => {
    switch (key) {
      case 'actions': {
        const excludeEditKeys: (keyof IMerchant)[] = [
          'id',
          'created_at',
          'updated_at',
          'deleted_at',
          'wallet_policy',
          'wallet_id',
          'bank_id',
          'is_test',
          'settings',
          'wallet_type',
        ];

        const excludeViewKeys: (keyof IMerchant)[] = [
          'deleted_at',
          'wallet_policy',
          'wallet_id',
          'bank_id',
          'settings',
          'wallet_type',
        ];

        const settings = merchant.settings;
        const normalizedMerchant = {
          id: merchant.id,
          ...settings,
        };

        const handleTransformFormData = (formData: FormData) => {
          const settings = assmebleMerchantSettings(formData);
          return {
            id: merchant.id,
            ...settings,
          };
        };

        const actionBtnsConfig: ITableActionsConfig = {
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title={`Edit Merchant ${merchant.short_name}`}
                entity={merchant}
                excludeKeys={excludeEditKeys}
                useEditEntityMutation={useEditMerchantMutation}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
          },
          'edit-settings': {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title={`Edit merchant settings for ${merchant.short_name}`}
                entity={normalizedMerchant}
                excludeKeys={['id']}
                useEditEntityMutation={useCreateMerchantSettingsMutation}
                refetch={refetch}
                renamedLabels={{
                  deviation: 'Deposit Amount Threshold (%)',
                  threshold: 'Min Deposit Amount',
                }}
                onTransformFormData={handleTransformFormData}
              />
            ),
            popupSize: 'md',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntity
                entity={merchant}
                title={`View Merchant ${merchant.short_name}`}
                excludeKeys={excludeViewKeys}
              />
            ),
            popupSize: 'md',
          },
          delete: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalDeleteEntity
                id={merchant.id}
                entityName={merchant.short_name}
                message='Do you want to delete this merchant?'
                deleteEntity={deleteMerchant}
                refetch={refetch}
              />
            ),
            popupSize: 'sm',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return merchant[key];
    }
  };

  const NewMerchant = () => <NewMerchantModal refetch={refetch} />;

  return {
    columnTitles,
    renderRow,
    NewMerchant,
  };
};
